import * as React from 'react';
import { Input } from './input';

/**
 * `<input type="number">` qu'on peut VIDER avant de retaper.
 *
 * Retour de Bryan (2026-09-09) : « si j'ai 5 par défaut et que je veux
 * mettre 40, je dois aller sur 4 puis ajouter 0, ou taper 40 puis supprimer
 * le 5 de 540 ». Cause : un champ contrôlé dont le parent fait
 * `parseInt(e.target.value) || 5` — vider le champ renvoie NaN, le parent
 * retombe sur 5, et React réécrit « 5 » dans le champ avant la frappe
 * suivante.
 *
 * Ici le texte tapé vit dans le champ tant qu'il a le focus : vider est
 * permis, le parent n'est prévenu que quand il y a une valeur. Au blur, un
 * champ vide reprend la valeur du parent. Au focus, le contenu est
 * sélectionné : taper « 40 » remplace « 5 » d'un coup.
 */
const NumberInput = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ value, onChange, onFocus, onBlur, ...props }, ref) => {
    const controlled = value !== undefined;
    const asText = (v: unknown) => (v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v)) ? '' : String(v));
    const [text, setText] = React.useState(asText(value));
    const [focused, setFocused] = React.useState(false);

    React.useEffect(() => {
      if (!focused) setText(asText(value));
    }, [value, focused]);

    return (
      <Input
        ref={ref}
        type="number"
        value={controlled ? text : undefined}
        onChange={(e) => {
          setText(e.target.value);
          if (e.target.value !== '') onChange?.(e);
        }}
        onFocus={(e) => {
          setFocused(true);
          e.target.select();
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          if (e.target.value === '') setText(asText(value));
          onBlur?.(e);
        }}
        {...props}
      />
    );
  }
);
NumberInput.displayName = 'NumberInput';

export { NumberInput };
